import React from 'react';
import {
    GST_DOCUMENT_MODES,
    getAllowedGstDocumentModes,
    getDefaultGstDocumentMode,
} from '../utils/einvoiceHelpers';

function StatusBadge({ value, emphasize = false }) {
    const isYes = Boolean(value);
    return (
        <span className={`gst-compliance-badge ${isYes ? 'gst-compliance-badge-yes' : 'gst-compliance-badge-no'}${emphasize ? ' gst-compliance-badge-emphasis' : ''}`}>
            {isYes ? 'Yes' : 'No'}
        </span>
    );
}

function ComplianceField({ label, children }) {
    return (
        <div className="gst-compliance-field">
            <span className="gst-compliance-label">{label}</span>
            <div className="gst-compliance-value">{children}</div>
        </div>
    );
}

function ReadOnlyText({ value, bold = false }) {
    return (
        <span className={`gst-compliance-text${bold ? ' gst-compliance-text-bold' : ''}`}>
            {value || '—'}
        </span>
    );
}

const MODE_LABELS = {
    [GST_DOCUMENT_MODES.NONE]: 'None (Save only)',
    [GST_DOCUMENT_MODES.EINVOICE]: 'E-Invoice only',
    [GST_DOCUMENT_MODES.EINVOICE_EWAY]: 'E-Invoice + E-Way Bill',
};

export default function InvoiceEinvoiceSection({
    config,
    einvoiceResult,
    onFetchIrn,
    onGenerateEinvoice,
    isFetchingIrn,
    selectedMode,
    onModeChange,
}) {
    if (!config) {
        return null;
    }

    const irn = einvoiceResult?.einvoice_irn || config?.einvoice_irn;
    const ack = einvoiceResult?.einvoice_ack || config?.einvoice_ack;
    const envBlocked = Boolean(config?.gst_environment_blocked);
    const canFetchIrn = Boolean(config?.is_einvoice_required && !envBlocked && !irn && onFetchIrn);
    const canGenerateEinvoice = Boolean(config?.can_generate_einvoice && !envBlocked && !irn && onGenerateEinvoice);
    const environment = (config.einvoice_environment || 'sandbox').toUpperCase();
    const irnSource = config.einvoice_api_configured ? config.irn_source : 'API not configured';
    const threshold = config.einvoice_threshold_limit
        ? config.einvoice_threshold_limit.toLocaleString('en-IN')
        : '0';
    const ewayLimit = config.eway_bill_gst_limit
        ? config.eway_bill_gst_limit.toLocaleString('en-IN')
        : '50,000';
    const mode = selectedMode || getDefaultGstDocumentMode(config);
    const allowedModes = getAllowedGstDocumentModes(config);
    const canSelectMode = Boolean(onModeChange) && !irn && allowedModes.length > 1;
    const wantsEway = mode === GST_DOCUMENT_MODES.EINVOICE_EWAY;

    return (
        <div className="gst-compliance-panel">
            <div className="gst-compliance-header">
                <h5 className="gst-compliance-title">E-Invoice / E-Way Bill (GST)</h5>
            </div>

            <div className="gst-compliance-meta">
                <ComplianceField label="Document Mode">
                    {canSelectMode ? (
                        <select
                            className="browser-default custom-select gst-document-mode-select"
                            value={mode}
                            onChange={(event) => onModeChange(event.target.value)}
                        >
                            {allowedModes.map((option) => (
                                <option key={option} value={option}>
                                    {MODE_LABELS[option] || option}
                                </option>
                            ))}
                        </select>
                    ) : (
                        <ReadOnlyText value={MODE_LABELS[mode] || mode} bold />
                    )}
                </ComplianceField>
                <ComplianceField label="GST API Environment">
                    <ReadOnlyText value={environment} bold />
                </ComplianceField>
                <ComplianceField label="Invoice Type">
                    <ReadOnlyText value={config.invoice_type || (config.has_buyer_gstin ? 'B2B' : 'B2C')} bold />
                </ComplianceField>
                <ComplianceField label="Buyer GSTIN">
                    <ReadOnlyText value={config.buyer_gst_no || 'Not available'} />
                </ComplianceField>
                <ComplianceField label="Company GSTIN">
                    <ReadOnlyText value={config.seller_gst_no || 'Not available'} />
                </ComplianceField>
                <ComplianceField label="IRN Source">
                    <ReadOnlyText value={irnSource} />
                </ComplianceField>
            </div>

            <div className="gst-compliance-columns">
                <div className="gst-compliance-column gst-compliance-column-einvoice">
                    <div className="gst-compliance-column-header">E-Invoice</div>

                    <ComplianceField label="Applicable">
                        <StatusBadge value={config.is_einvoice_applicable} />
                    </ComplianceField>

                    <ComplianceField label="Valid From">
                        <ReadOnlyText value={config.einvoice_valid_from || 'Not set'} />
                    </ComplianceField>

                    <ComplianceField label="Threshold Limit">
                        <ReadOnlyText value={threshold} />
                    </ComplianceField>

                    <ComplianceField label="Generate on Save">
                        <StatusBadge
                            value={config.can_generate_einvoice && mode !== GST_DOCUMENT_MODES.NONE}
                            emphasize
                        />
                    </ComplianceField>

                    <ComplianceField label="Rule">
                        <ReadOnlyText value={config.einvoice_reason || ''} />
                    </ComplianceField>

                    {envBlocked && (
                        <div className="gst-compliance-env-warning">
                            <strong>Sandbox / GSTIN mismatch</strong>
                            <p>{config.gst_environment_error}</p>
                        </div>
                    )}

                    {!config.has_buyer_gstin && config.is_einvoice_applicable && (
                        <ComplianceField label="B2C Note">
                            <ReadOnlyText value={config.einvoice_skip_reason || 'Save without e-Invoice until buyer GSTIN is available.'} />
                        </ComplianceField>
                    )}

                    {irn && (
                        <>
                            <ComplianceField label="IRN">
                                <ReadOnlyText value={irn} bold />
                            </ComplianceField>
                            <ComplianceField label="Ack No">
                                <ReadOnlyText value={ack} />
                            </ComplianceField>
                        </>
                    )}

                    {canGenerateEinvoice && (
                        <div className="gst-compliance-fetch-irn">
                            <button
                                type="button"
                                className="btn btn-sm btn-success"
                                onClick={onGenerateEinvoice}
                                disabled={isFetchingIrn}
                            >
                                {isFetchingIrn ? 'Generating E-Invoice...' : 'Generate E-Invoice'}
                            </button>
                            <p className="gst-compliance-fetch-hint">
                                Use after customer GSTIN is updated in Customer Master and this invoice is saved.
                            </p>
                        </div>
                    )}

                    {canFetchIrn && (
                        <div className="gst-compliance-fetch-irn">
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={onFetchIrn}
                                disabled={isFetchingIrn}
                            >
                                {isFetchingIrn ? 'Fetching IRN...' : 'Fetch IRN from GSP'}
                            </button>
                            <p className="gst-compliance-fetch-hint">
                                Use when e-invoice was posted to GST but IRN/Ack were not saved on this invoice.
                            </p>
                        </div>
                    )}
                </div>

                <div className="gst-compliance-column gst-compliance-column-eway">
                    <div className="gst-compliance-column-header">E-Way Bill</div>

                    <ComplianceField label="Company Enabled">
                        <StatusBadge value={config.is_eway_bill_applicable} />
                    </ComplianceField>

                    <ComplianceField label="Selected for this invoice">
                        <StatusBadge value={wantsEway} emphasize />
                    </ComplianceField>

                    <ComplianceField label="GST Limit">
                        <ReadOnlyText value={`Rs. ${ewayLimit}`} />
                    </ComplianceField>

                    <ComplianceField label="Amount Rule">
                        <StatusBadge value={config.is_eway_bill_required} />
                    </ComplianceField>

                    <ComplianceField label="Rule">
                        <ReadOnlyText
                            value={
                                wantsEway
                                    ? (config.eway_reason || 'E-Way Bill will be generated with / after IRN.')
                                    : 'E-Way Bill not selected for this invoice.'
                            }
                        />
                    </ComplianceField>
                </div>
            </div>
        </div>
    );
}
